<template>
  <b-container
    class="mb-5"
    fluid
  >
    <div class="d-flex justify-content-end mb-3 pb-0">
      <b-breadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <b-card>
      <b-row class="mb-2">
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group>
            <label>
              <strong>Search</strong>
            </label>
            <b-input
              v-model="tableEmployees.filter.search"
              type="text"
              placeholder="search here"
              debounce="1000"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <div class="form-group">
            <label>
              <strong>Company</strong>
            </label>
            <b-select
              v-model="tableEmployees.filter.company"
              :options="filterCompanies"
            />
          </div>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <div class="form-group">
            <label>
              <strong>Organization</strong>
            </label>
            <b-select
              v-model="tableEmployees.filter.organization"
              :options="filterOrganizations"
            />
          </div>
        </b-col>
      </b-row>

      <b-table
        ref="tableEmployees"
        hover
        responsive
        class="mt-2"
        :per-page="tableEmployees.perPage"
        :current-page="tableEmployees.currentPage"
        :items="tableProvider"
        :fields="tableEmployees.fields"
        :sort-by.sync="tableEmployees.sortBy"
        :sort-desc.sync="tableEmployees.sortDesc"
        :sort-direction="tableEmployees.sortDirection"
        :filter="tableEmployees.filter"
        :filter-included-fields="tableEmployees.filterOn"
        show-empty
      >
        <template #cell(index)="data">
          <div class="text-center">
            {{ tableEmployees.currentPage * tableEmployees.perPage - tableEmployees.perPage + (data.index + 1) }}
          </div>
        </template>

        <template #cell(active)="data">
          {{ data.item.active == 1 ? 'Yes' : 'No' }}
        </template>

        <template #cell()="data">
          <div class="text-nowrap">
            {{ data.value }}
          </div>
        </template>

        <template #cell(actions)="row">
          <div class="text-nowrap text-center">
            <b-button
              size="sm"
              variant="primary"
              @click="onEdit(row.item)"
            >
              Edit
            </b-button>
          </div>
        </template>

      </b-table>

      <b-row>
        <b-col
          cols="12"
          sm="6"
        >
          <div class="w-100 w-sm-25 mb-2 sm-mb-2">
            <b-select
              v-model="tableEmployees.perPage"
              :options="tableEmployees.pageOptions"
              size="sm"
            />
          </div>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          class="d-flex justify-content-center justify-content-sm-end"
        >
          <b-pagination
            v-model="tableEmployees.currentPage"
            :total-rows="tableEmployees.totalRows"
            :per-page="tableEmployees.perPage"
            first-number
            last-number
            pills
            prev-text="Prev"
            next-text="Next"
            aria-controls="table"
          />
        </b-col>
      </b-row>
    </b-card>

    <b-modal
      id="modal-employee-form"
      scrollable
      no-close-on-backdrop
      no-enforce-focus
      aria-hidden="false"
      title="Edit Employee"
      @ok="onValidate"
    >
      <ValidationObserver
        ref="formEmployee"
      >
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <ValidationProvider
            #default="{ errors }"
            name="first name"
            vid="first_name"
            rules="required|max:50"
          >
            <b-form-group>
              <label for="first_name">
                <strong>First Name</strong>
              </label>
              <b-input
                id="first_name"
                v-model="employee.first_name"
                type="text"
                placeholder="enter first name"
                autocomplete="off"
                disabled
                :state="errors.length > 0 ? false : null"
              />
              <div
                v-if="errors.length > 0"
                class="invalid-feedback"
                v-text="errors[0]"
              />
            </b-form-group>
          </ValidationProvider>

          <ValidationProvider
            #default="{ errors }"
            name="middle name"
            vid="middle_name"
            rules=""
          >
            <b-form-group>
              <label for="middle_name">
                <strong>Middle Name</strong>
              </label>
              <b-input
                id="middle_name"
                v-model="employee.middle_name"
                type="text"
                placeholder="enter middle name"
                autocomplete="off"
                disabled
                :state="errors.length > 0 ? false : null"
              />
              <div
                v-if="errors.length > 0"
                class="invalid-feedback"
                v-text="errors[0]"
              />
            </b-form-group>
          </ValidationProvider>

          <ValidationProvider
            #default="{ errors }"
            name="last name"
            vid="last_name"
            rules="required|max:50"
          >
            <b-form-group>
              <label for="last_name">
                <strong>Last Name</strong>
              </label>
              <b-input
                id="last_name"
                v-model="employee.last_name"
                type="text"
                placeholder="enter last name"
                autocomplete="off"
                disabled
                :state="errors.length > 0 ? false : null"
              />
              <div
                v-if="errors.length > 0"
                class="invalid-feedback"
                v-text="errors[0]"
              />
            </b-form-group>
          </ValidationProvider>

          <ValidationProvider
            #default="{ errors }"
            name="email"
            vid="email"
            rules="required|email|max:100"
          >
            <b-form-group>
              <label for="email">
                <strong>Email</strong>
              </label>
              <b-input
                id="email"
                v-model="employee.email"
                type="text"
                placeholder="enter email"
                autocomplete="off"
                disabled
                :state="errors.length > 0 ? false : null"
              />
              <div
                v-if="errors.length > 0"
                class="invalid-feedback"
                v-text="errors[0]"
              />
            </b-form-group>
          </ValidationProvider>

          <ValidationProvider
            #default="{ errors }"
            name="password"
            vid="password"
            rules="min:8|confirmed:password_confirmation"
          >
            <b-form-group
              description="Leave blank when not changing password"
            >
              <label for="password">
                <strong>Password</strong>
              </label>
              <b-input
                id="password"
                v-model="employee.password"
                type="password"
                placeholder="enter password"
                autocomplete="off"
                :state="errors.length > 0 ? false : null"
              />
              <div
                v-if="errors.length > 0"
                class="invalid-feedback"
                v-text="errors[0]"
              />
            </b-form-group>
          </ValidationProvider>

          <ValidationProvider
            #default="{ errors }"
            name="password confirmation"
            vid="password_confirmation"
            rules="min:8"
          >
            <b-form-group
              description="Leave blank when not changing password"
            >
              <label for="password_confirmation">
                <strong>Password Confirmation</strong>
              </label>
              <b-input
                id="password_confirmation"
                v-model="employee.password_confirmation"
                type="password"
                placeholder="enter password confirmation"
                autocomplete="off"
                :state="errors.length > 0 ? false : null"
              />
              <div
                v-if="errors.length > 0"
                class="invalid-feedback"
                v-text="errors[0]"
              />
            </b-form-group>
          </ValidationProvider>

          <div
            class="form-group"
          >
            <b-form-checkbox
              id="is_active"
              v-model="employee.active"
              name="is_active"
              value="1"
              unchecked-value="0"
              switch
              :disabled="state.busy"
            >
              <strong>Is Active?</strong>
            </b-form-checkbox>
          </div>

        </form>
      </ValidationObserver>

      <template #modal-footer="{ok, cancel}">
        <b-button
          variant="success"
          :disabled="state.busy"
          @click="ok()"
        >
          Update Record
        </b-button>
        <b-button
          variant="outline-dark"
          :disabled="state.busy"
          @click="cancel()"
        >
          Close Window
        </b-button>
      </template>
    </b-modal>
  </b-container>
</template>

<script>
import _ from 'lodash'
import { core } from '@/config/pluginInit'
import { AxiosError, SAdminEmployee, SSharedList } from '@/services'
import formatter from '@/mixins/formatter'

export default {
  name: 'VAdminEmployeeAccounts',

  middleware: ['auth', 'admin'],

  metaInfo () {
    return {
      title: 'Employee Accounts'
    }
  },

  mixins: [formatter],

  data () {
    return {
      state: {
        busy: false
      },
      list: {
        companies: [],
        organizations: []
      },
      employee: {
        id: 0,
        email: '',
        password: '',
        first_name: '',
        middle_name: '',
        last_name: '',
        branch_name: '',
        position_name: '',
        active: 0
      },
      tableEmployees: {
        perPage: 10,
        pageOptions: [10, 25, 50, 100],
        totalRows: 0,
        currentPage: 1,
        sortBy: '',
        sortDesc: false,
        sortDirection: 'asc',
        filter: {
          search: '',
          company: 'All',
          organization: 'All'
        },
        filterOn: [],
        fields: [
          'index',
          'actions',
          { key: 'created_at', formatter: this.dateTimeFormatter, sortable: true },
          { key: 'email', sortable: true },
          { key: 'first_name', sortable: true },
          { key: 'middle_name', sortable: false },
          { key: 'last_name', sortable: true },
          { key: 'company_name', sortable: false, label: 'Company' },
          { key: 'organization_name', sortable: false, label: 'Organization' },
          { key: 'active' },
          { key: 'updated_at', formatter: this.dateTimeFormatter, sortable: true }
        ]
      }
    }
  },

  computed: {

    filterOrganizations () {
      const organizations = this.list.organizations.filter(
        organization => {
          const company = this.tableEmployees.filter.company
          if (company !== 'All') {
            return organization.company === company
          }
          return organization
        }
      )
      return [{ value: 'All', text: 'All' }].concat(organizations)
    },

    filterCompanies () {
      return [{ value: 'All', text: 'All' }].concat(this.list.companies)
    }

  },

  watch: {
    'tableEmployees.filter.company' () {
      this.tableEmployees.filter.organization = 'All'
    }
  },

  mounted () {
    core.index()
    this.getOptions()
  },

  methods: {
    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableEmployees.busy = true
      const filters = this.objectToUrl({
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        filter_company: filter.company,
        filter_organization: filter.organization
      })

      return await SAdminEmployee.get(filters).then(
        ({ data }) => {
          this.tableEmployees.totalRows = data.total_rows
          return data.items
        }
      )
        .catch(() => {
          this.tableEmployees.totalRows = 0
          return []
        })
        .finally(() => {
          this.tableEmployees.busy = false
        })
    },

    async getOptions () {
      await SSharedList.getCompanies().then(
        ({ data }) => {
          this.list.companies = data.map(
            ({ id, company_name }) => ({
              text: company_name,
              value: id
            })
          )
        }
      ).catch(() => {
        this.list.companies = []
      })

      await SSharedList.getOrganizations().then(
        ({ data }) => {
          this.list.organizations = data.map(
            ({ company_id, id, organization_name }) => ({
              company: company_id,
              text: organization_name,
              value: id
            })
          )
        }
      ).catch(() => {
        this.list.organizations = []
      })
    },

    onEdit (item) {
      this.state.editing = true
      this.employee.id = item.id
      this.employee.email = item.email
      this.employee.password = ''
      this.employee.password_confirmation = ''
      this.employee.first_name = item.first_name
      this.employee.middle_name = item.middle_name
      this.employee.last_name = item.last_name
      this.employee.branch_name = item.branch_name
      this.employee.grade_name = item.grade_name
      this.employee.position_name = item.position_name
      this.employee.active = item.active
      this.$bvModal.show('modal-employee-form')
    },

    async onValidate (bvModalEvt) {
      bvModalEvt.preventDefault()
      await this.$refs.formEmployee.validate().then(
        async areAllFieldsValid => {
          if (areAllFieldsValid) {
            this.$swal.fire({
              icon: 'question',
              title: 'Confirmation',
              text: this.state.editing ? 'Update Employee?' : 'Create Employee?',
              confirmButtonColor: '#06C270',
              confirmButtonText: 'Confirm',
              cancelButtonColor: '#737373',
              showCancelButton: true,
              showLoaderOnConfirm: true,
              preConfirm: () => {
                return this.onPut()
              },
              allowOutsideClick: () => !this.$swal.isLoading()
            })
          } else {
            this.$bvModal.msgBoxOk('Oops! There were problem with your inputs.', {
              title: 'Validation Error',
              size: 'sm',
              buttonSize: 'sm',
              okVariant: 'danger',
              centered: true
            })
          }
        }
      )
    },

    async onPut () {
      this.state.busy = true
      return new Promise(
        resolve => {
          SAdminEmployee.put(this.employee).then(
            ({ data }) => {
              this.$bvModal.hide('modal-employee-form')
              this.$swal.fire({
                icon: 'success',
                title: 'Successful',
                text: data.message,
                confirmButtonColor: '#06C270',
                confirmButtonText: 'Dismiss'
              }).then(() => {
                const row = _.find(this.$refs.tableEmployees.localItems, {
                  id: data.employee.id
                })
                row.email = data.employee.email
                row.first_name = data.employee.first_name
                row.middle_name = data.employee.middle_name
                row.last_name = data.employee.last_name
                row.company_name = data.employee.company_name
                row.organization_name = data.employee.organization_name
                row.position_name = data.employee.position_name
                row.active = data.employee.active
                row.updated_at = data.employee.updated_at
              })
            }
          ).catch(
            error => {
              if (error instanceof AxiosError) {
                if (error.code === 422) {
                  this.$refs.formEmployee.setErrors(error.message)
                }
              }
            }
          ).finally(resolve)
        }
      ).then(() => {
        this.state.busy = false
      })
    }
  }
}
</script>
